import React, { useCallback, useEffect, useState } from "react";
import Select from "react-select";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { apiKey } from "../../../config/config";
import MessageCard from "../../../components/AdminCards/MessageCard";
import { toast } from "react-toastify";

const generateReferenceNumber = () => {
  // Generate a random number between 100000000 and 999999999
  return Math.floor(100000000 + Math.random() * 900000000).toString();
};

function AddExpensesForm() {
  const user = JSON.parse(localStorage.getItem("rms-admin"));
  const { id } = useParams();
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split("T")[0], // Format: YYYY-MM-DD
    amount: "",
    vat_percentage: "",
    tax_method: "",
    total_amount: "",
    vat: "",
    reference: generateReferenceNumber(),
    category_id: "",
    payment_method_id: "",
    note: "",
    employee_id: "",
    user_id: user.user.id,
    outlet_id: user.user.outlet_id,
  });
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const handleChange = (e) =>
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!id) {
      axios
        .post(`${apiKey}/api/rms/admin/expense`, formData, {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        })
        .then((res) => {
          toast.success("Success", {
            position: "bottom-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
          });
          navigate(-1);
        })
        .catch((err) => {
          toast.error(err.response.data.message, {
            position: "bottom-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
          });
        })
        .finally(() => setLoading(false));
    } else {
      axios
        .put(`${apiKey}/api/rms/admin/expense/${id}`, formData, {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        })
        .then((res) => {
          toast.success("Success", {
            position: "bottom-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
          });
          navigate(-1);
        })
        .catch((err) => {
          toast.error(err.response.data.message, {
            position: "bottom-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
          });
        })
        .finally(() => setLoading(false));
    }
  };

  const fetchExpenseCat = useCallback(() => {
    axios
      .get(`${apiKey}/api/rms/admin/expensescategory`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
        params: {
          outletId: user?.user.outlet_id,
        },
      })
      .then((res) => {
        setCategories(
          res.data.map((cat, index) => ({
            label: cat.name,
            vat_percentage: cat.vat_percentage,
            value: cat.id,
            tax_method: cat.tax_method,
            index,
          }))
        );
      })
      .catch((err) => {
        setError("Cannot load data");
      })
      .finally(() => {
        // setPageLoading(false);
      });
  }, [user?.token, user?.user.outlet_id]);

  const fetchUsers = useCallback(() => {
    axios
      .get(`${apiKey}/api/rms/admin/user`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
        params: {
          outletId: user?.user.outlet_id
        }
      })
      .then((res) => {
        setUsers(
          res.data.map((user) => ({
            label: user.name,
            value: user.id,
          }))
        );
      })
      .catch((err) => {
        setError("Cannot load data");
      })
      .finally(() => {
        // setPageLoading(false);
      });
  }, [user?.token, user?.user.outlet_id]);

  const fetchPayment = useCallback(() => {
    axios
      .get(`${apiKey}/api/rms/admin/get-all-payment`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      })
      .then((res) => {
        setPaymentMethods(
          res.data.map((method, index) => {
            // if (method.id >= 5) return;
            return {
              label: method.name,
              value: method.id,
              index,
            };
          })
        );
      })
      .catch((err) => {
        setError("Cannot load data");
      })
      .finally(() => {
        // setPageLoading(false);
      });
  }, [user?.token]);

  useEffect(() => {
    fetchExpenseCat();
    fetchUsers();
    fetchPayment();
  }, [fetchExpenseCat, fetchPayment, fetchUsers]);

  useEffect(() => {
    setFormData({
      ...formData,
      vat_percentage: categories[formData.cat_index]?.vat_percentage || "",
      tax_method: categories[formData.cat_index]?.tax_method || "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, formData.category_id]);

  useEffect(() => {
    if (formData.amount > 0 && formData.vat_percentage > 0) {
      let vat = 0;
      let baseAmount = 0;
      let totalAmount = 0;

      if (formData.tax_method.toLowerCase() === "inclusive") {
        // VAT Inclusive
        baseAmount =
          parseFloat(formData.amount) / (1 + formData.vat_percentage / 100); // Value before VAT
        vat = parseFloat(formData.amount) - baseAmount; // VAT Value
        totalAmount = parseFloat(formData.amount); // Already inclusive of VAT
      } else if (formData.tax_method.toLowerCase() === "exclusive") {
        // VAT Exclusive
        vat = parseFloat(formData.amount) * (formData.vat_percentage / 100); // VAT Value
        baseAmount = parseFloat(formData.amount); // Value before VAT
        totalAmount = parseFloat(formData.amount) + vat; // Total with VAT
      }

      // Update state with calculated values
      setFormData((prevData) => ({
        ...prevData,
        base_amount: baseAmount.toFixed(2), // Value before VAT
        vat: vat.toFixed(2), // VAT Value
        total_amount: totalAmount.toFixed(2), // Total Value
      }));
    }
  }, [formData.amount, formData.vat_percentage, formData.tax_method]);

  useEffect(() => {
    if (id) {
      setPageLoading(true);
      const fetchExpenseCat = () => {
        axios
          .get(`${apiKey}/api/rms/admin/expense/${id}`, {
            headers: {
              Authorization: `Bearer ${user?.token}`,
            },
          })
          .then((res) => {
            setFormData(res.data);
          })
          .catch((err) => {
            setError(err.response.data.message);
          })
          .finally(() => setPageLoading(false));
      };
      fetchExpenseCat();
    }
  }, [id, user?.token]);

  if (pageLoading) {
    return (
      <div className="absolute top-0 left-0 w-full h-full grid place-items-center">
        <img src="/assets/gif/dash2.gif" alt="" />
      </div>
    );
  }

  if (error) {
    return <MessageCard error={error} />;
  }

  return (
    <main>
      <div className="relative my-16 flex flex-col min-w-0 break-words w-full mb-6 shadow-lg bg-white rounded-lg p-4 border-0">
        <div className="flex justify-between items-center mb-4">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-[#7B20C7] text-2xl"> Add Expenses</h1>
          </div>
        </div>
        <div className="w-full">
          <form action="" className="w-full" onSubmit={handleSubmit}>
            <div className="flex flex-col md:flex-row gap-x-8 gap-y-2 w-full ">
              <label htmlFor="" className="w-full md:w-1/3">
                <span className="text-[#7B20C7] text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2 font-bold">
                  Date
                </span>
                <div className="mb-2 pt-0">
                  <input
                    type="text"
                    placeholder="2024-01-19"
                    className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-gray-100 rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                    required
                    value={formData.date}
                    onChange={handleChange}
                    disabled
                  />
                </div>
              </label>

              <label htmlFor="" className="w-full md:w-1/3">
                <span className="text-[#7B20C7] text-sm mb-2 font-bold">
                  Responsible Person
                </span>
                <div className="mb-2 pt-0">
                  <Select
                    type="text"
                    placeholder="Select"
                    className="placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full "
                    required
                    options={users}
                    defaultValue={{
                      value: formData.employee?.id,
                      label: formData.employee?.name,
                    }}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        employee_id: e.value,
                      })
                    }
                  />
                </div>
              </label>

              <label htmlFor="" className="w-full md:w-1/3">
                <span className="text-[#7B20C7] text-sm mb-2 font-bold">
                  Reference No
                </span>
                <div className="mb-2 pt-0">
                  <input
                    type="text"
                    placeholder="429945702"
                    className=" px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-gray-100 rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full "
                    required
                    value={formData.reference}
                    onChange={handleChange}
                    disabled
                  />
                </div>
              </label>
            </div>

            <div className="flex flex-col md:flex-row gap-x-8 gap-y-2 w-full ">
              <label htmlFor="" className="w-full md:w-1/3">
                <span className="text-[#7B20C7] text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2  font-bold">
                  Category
                </span>
                <div className="mb-2 pt-0">
                  <Select
                    type="text"
                    placeholder="Select"
                    className="placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                    required
                    options={categories}
                    defaultValue={{
                      value: formData.category?.id,
                      label: formData.category?.name,
                    }}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        category_id: e.value,
                        cat_index: e.index,
                      })
                    }
                  />
                </div>
              </label>

              <label htmlFor="" className="w-full md:w-1/3">
                <span className="text-[#7B20C7] text-sm mb-2  font-bold">
                  Vat Percentage
                </span>
                <div className="mb-2 pt-0">
                  <input
                    type="text"
                    placeholder="Vat Percentage"
                    className=" px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-gray-100 rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full "
                    required
                    value={formData.vat_percentage}
                    onChange={handleChange}
                    disabled
                  />
                </div>
              </label>

              <label htmlFor="" className="w-full md:w-1/3">
                <span className="text-[#7B20C7] text-sm mb-2 font-bold">
                  Tax method
                </span>
                <div className="mb-2 pt-0">
                  <input
                    type="text"
                    placeholder="Tax method"
                    className=" px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-gray-100 rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full "
                    name="tax_method"
                    required
                    value={formData.tax_method}
                    onChange={handleChange}
                    disabled
                  />
                </div>
              </label>
            </div>

            <div className="flex flex-col md:flex-row gap-x-8 gap-y-2 w-full ">
              <label htmlFor="" className="w-full md:w-1/3">
                <span className="text-[#7B20C7] text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2   font-bold">
                  Amount
                </span>
                <div className="mb-2 pt-0 ">
                  <input
                    type="text"
                    placeholder="Amount"
                    className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                    required
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                  />
                </div>
              </label>

              <label htmlFor="" className="w-full md:w-1/3 ">
                <span className="text-[#7B20C7] text-sm mb-2  font-bold">
                  Paymeny method
                </span>
                <div className="mb-2 pt-0 pr-1">
                  <Select
                    type="text"
                    placeholder="Select"
                    className="placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full "
                    required
                    options={paymentMethods}
                    defaultValue={{
                      value: formData.payment_method?.id,
                      label: formData.payment_method?.name,
                    }}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        payment_method_id: e.value,
                      })
                    }
                  />
                </div>
              </label>

              <label htmlFor="" className="w-full md:w-1/3 ">
                <span className="text-[#7B20C7] text-sm mb-2  font-bold">
                  Note
                </span>
                <div className="mb-2 pt-0 pr-1">
                  <textarea
                    rows="3"
                    type="text"
                    placeholder="Select"
                    name="note"
                    className=" px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full "
                    onChange={handleChange}
                    value={formData.note}
                  />
                </div>
              </label>
            </div>

            <div className="flex flex-col md:flex-row gap-x-8 gap-y-2 w-full ">
              <label htmlFor="" className="w-full md:w-1/3">
                <span className="text-[#7B20C7] text-sm mb-2 font-bold">
                  Total amount with vat
                </span>
                <div className="mb-2 pt-0 ">
                  <input
                    type="text"
                    placeholder="Amount"
                    className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-gray-100 rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                    value={formData.total_amount}
                    onChange={handleChange}
                    disabled
                  />
                </div>
              </label>

              <label htmlFor="" className="w-full md:w-1/3 ">
                <span className="text-[#7B20C7] text-sm mb-2  font-bold">
                  Vat
                </span>
                <div className="mb-2 pt-0 pr-1">
                  <input
                    type="text"
                    placeholder="Vat"
                    className=" px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-gray-100 rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full "
                    required
                    value={formData.vat}
                    onChange={handleChange}
                    disabled
                  />
                </div>
              </label>

              <label htmlFor="" className="w-full md:w-1/3 "></label>
            </div>

            <span className="w-full bg-[#7B20C7] h-1 my-2 inline-block"></span>
            <div className="flex gap-2">
              <button
                className="bg-[#7B20C7] text-white text-sm px-3 py-1 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 text-center w-fit relative"
                type="submit"
              >
                {loading && (
                  <span className="absolute top-0 left-0 w-full h-full py-1 px-3 bg-black bg-opacity-50">
                    <img
                      className="w-4 mx-auto"
                      src="https://i.gifer.com/ZKZx.gif"
                      alt=""
                    />
                  </span>
                )}
                Submit
              </button>
              <button
                className="bg-[#7B20C7] text-white text-sm px-3 py-1 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 text-center w-fit"
                type="button"
                onClick={() => navigate(-1)}
              >
                Back
              </button>
            </div>
          </form>
        </div>
      </div>
    </main>
  );
}

export default AddExpensesForm;
